import {isFeatureEnabled} from 'feature-flags';
import {Form, FormField, FormSubmitHandler, Separator} from 'platform/components';
import {Grid} from 'platform/foundation';

import {DeepPartial} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useCreateUserMutation,
  useGetCurrentUserInfoQuery,
  useGetOtherUserSettingsQuery,
  useGetUserQuery,
  usePatchUserMutation,
  useSetOtherUserSettingsItemMutation,
} from '@dms/api/customer';
import {UserResponseBody} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, is409UserDeactivatedError, openReactivateUserModal} from '@dms/shared';

import {ApiError, buildObject, isNotNilOrEmpty, useNavigate} from 'shared/web';

import {UserBranches} from './components/UserBranches';
import {UserInfo} from './components/UserInfo';
import {UserRoles} from './components/UserRoles';
import {MANAGER_ROLE_NAME} from './constants/MANAGER_ROLE_NAME';
import {useSettingsHeaderActions} from './hooks/useSettingsHeaderActions';
import {FormValues, getSchema} from './types/FormValues';

type SubmitDataType = {
  firstName: string;
  lastName: string;
  roles: string[];
  defaultBranchId: string;
  branchIds: string[];
};

export function UserDetail() {
  const {id: userId} = useParams();
  const isCreating = isNil(userId);
  const navigate = useNavigate();

  const {data: user, isLoading, isError} = useGetUserQuery({id: userId ?? ''}, {skip: !userId});
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const isCurrentUserManager = currentUser?.roles?.some((item) => item.title === MANAGER_ROLE_NAME);
  const isViewedUserManger = user?.roles?.some((role) => role.title === MANAGER_ROLE_NAME);
  const shouldIncludeRoles = isCurrentUserManager || !isViewedUserManger;

  const [patchUser] = usePatchUserMutation();
  const [createUser] = useCreateUserMutation();
  const [saveOtherUserSetting] = useSetOtherUserSettingsItemMutation();

  const isInsuranceComparisonEnabled = isFeatureEnabled(featureFlags.SALES_INSURANCE_COMPARISON_V1);
  const isUserCustomIdEnabled = isFeatureEnabled(featureFlags.CORE_CUSTOM_USER_ID);

  const {data: userSettings} = useGetOtherUserSettingsQuery(
    {userId: userId ?? ''},
    {skip: (!isUserCustomIdEnabled && !isInsuranceComparisonEnabled) || !userId}
  );

  const defaultValues: DeepPartial<FormValues> = {
    branchIds: user?.branches?.map((item) => item.id) ?? [],
    defaultBranchId: user?.defaultBranch?.id,
    roles: user?.roles?.map((item) => item.id) ?? [],
    firstName: user?.firstName,
    lastName: user?.lastName,
    insurerNumber: userSettings?.insurerNumber?.value?.toString() ?? undefined,
    customId: userSettings?.customId?.value?.toString() ?? undefined,
  };

  const actions = useSettingsHeaderActions(user);

  const updateOtherSettings = (data: FormValues) => async (updateData: UserResponseBody) => {
    const id = isCreating ? updateData.id : userId;

    if ((data.customId || null) !== (defaultValues.customId || null)) {
      await saveOtherUserSetting({
        userId: id,
        settingKey: 'custom_id',
        body: {
          value: data.customId ?? '',
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    if ((data.insurerNumber || null) !== (defaultValues.insurerNumber || null)) {
      await saveOtherUserSetting({
        userId: id,
        settingKey: 'insurer_number',
        body: {
          value: data.insurerNumber ?? '',
        },
      })
        .unwrap()
        .catch(handleApiError);
    }
  };

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const handleUpdate = () => {
      const submitData = buildObject<SubmitDataType>()
        .firstName(data.firstName)
        .lastName(data.lastName)
        .roles(data.roles, shouldIncludeRoles)
        .defaultBranchId(data.defaultBranchId)
        .branchIds(data.branchIds)
        .build();

      return isNil(userId)
        ? createUser({
            createUserRequestBody: {
              ...submitData,
              password: data.password as string,
              email: data.email as string,
              pins: [],
            },
          })
        : patchUser({
            userId,
            updateUserRequestBody: submitData,
          });
    };

    await handleUpdate()
      .unwrap()
      .then(updateOtherSettings(data))
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.userManagement);
      })
      .catch((error: ApiError) => {
        if (
          is409UserDeactivatedError(error) &&
          isNotNilOrEmpty(data.email) &&
          isNotNilOrEmpty(data.password)
        ) {
          openReactivateUserModal({
            reactivateUserRequestBody: {
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              password: data.password,
              roles: data.roles,
              defaultBranchId: data.defaultBranchId,
              branchIds: data.branchIds,
            },
            onSuccess: () => {
              if (blockerContext?.wasSubmittedFromBlocker) {
                return;
              }
              {
                return;
              }
              navigate(settingsRoutes.userManagement);
            },
          });
        } else {
          handleApiError(error, {signalApiError: blockerContext?.reportApiFailure});
        }
      });
  };

  return (
    <SettingsTemplate
      isCreating={isCreating}
      header={{
        title: user?.email ?? i18n.t('page.employeeDetail.labels.createNewUser'),
        breadcrumbs: [
          {label: i18n.t('entity.user.labels.users'), href: settingsRoutes.userManagement},
        ],
        actions,
      }}
      data-testid={testIds.settings.userManagementDetailV2('UserDetail')}
      isLoading={isLoading}
      isError={isError}
    >
      <SettingsSection>
        <Form<FormValues>
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          experimentalZodSchema={getSchema(isCreating ? 'create' : 'update')}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => {
            const handleCancel = () => navigateWithoutBlocker(settingsRoutes.userManagement);

            return (
              <>
                <UserInfo
                  data-testid={testIds.settings.userManagementDetailV2('UserInfo')}
                  control={control}
                  isCreating={isCreating}
                />

                <Separator />

                <UserBranches
                  data-testid={testIds.settings.userManagementDetailV2('UserBranches')}
                  control={control}
                  formApi={formApi}
                />

                <Separator />

                <UserRoles
                  data-testid={testIds.settings.userManagementDetailV2('UserRoles')}
                  userId={userId}
                  control={control}
                  formApi={formApi}
                />

                <Separator />

                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="insurerNumber"
                    label={i18n.t('page.settings.labels.insurerNumber')}
                  />

                  <FormField
                    control={control}
                    type="text"
                    name="customId"
                    label={i18n.t('page.settings.labels.customUserId')}
                  />
                </Grid>

                <SettingsFooter
                  actions={[
                    {
                      variant: 'secondary',
                      type: 'button',
                      onClick: handleCancel,
                      title: i18n.t('general.actions.cancel'),
                      'data-testid': testIds.settings.userManagementDetailV2('cancel'),
                    },
                    {
                      type: 'form-button',
                      title: isCreating
                        ? i18n.t('general.actions.create')
                        : i18n.t('general.actions.save'),
                      'data-testid': testIds.settings.userManagementDetailV2('submit'),
                      control,
                      buttonType: 'submit',
                    },
                  ]}
                />
              </>
            );
          }}
        </Form>
      </SettingsSection>
    </SettingsTemplate>
  );
}
